import React, { useEffect, useState, useRef } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { KantoorCard } from "@/components/KantoorCard";
import { PartnerCard } from "@/components/PartnerCard";
import { CheckCircle2, Loader2, ArrowRight, BookmarkPlus, ShieldCheck, Heart, Headphones, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const klantId = params.get("klant_id");
  const [status, setStatus] = useState("checking");
  const [matches, setMatches] = useState([]);
  const [partners, setPartners] = useState([]);
  const attemptsRef = useRef(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          const id = r.data.klant_id || klantId;
          if (id) {
            const m = await api.get(`/matches/klant/${id}`);
            setMatches(m.data);
          }
          // Load partners (independent of match data)
          api.get("/partners").then((p) => setPartners(p.data)).catch(() => {});
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) { console.error(e); setStatus("error"); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, klantId]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 bg-gradient-to-b from-amber-50/40 via-white to-[#F8FAFC]">
        {status === "checking" && (
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="payment-checking">
            <Loader2 className="mx-auto text-[#F5A623] animate-spin" size={48} />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-6">{t("payment_success.checking")}</h1>
            <p className="text-slate-600 mt-2">{t("payment_success.checking_subtitle")}</p>
          </div>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="payment-error">
            <h1 className="font-heading text-3xl font-bold text-[#1A2B4A]">{t("payment_success.error_title")}</h1>
            <p className="text-slate-600 mt-3">
              {status === "expired" ? t("payment_success.error_expired") : t("payment_success.error_generic")}
            </p>
            <Link to="/onboarding" className="btn-amber mt-6 inline-flex" data-testid="retry-payment">
              {t("payment_success.retry")}
            </Link>
          </div>
        )}

        {status === "paid" && (
          <>
            {/* HERO — celebration */}
            <section className="relative overflow-hidden">
              <div className="absolute inset-0 -z-10 pointer-events-none">
                <div className="absolute -top-32 -right-20 w-[440px] h-[440px] rounded-full bg-[#F5A623]/15 blur-3xl" />
                <div className="absolute -bottom-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#1A2B4A]/10 blur-3xl" />
              </div>
              <div className="max-w-6xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-8">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  <div data-testid="payment-success">
                    <div className="inline-flex items-center gap-2 pill-amber" data-testid="celebration-badge">
                      <CheckCircle2 size={14} className="text-[#22C55E]" />
                      <span>Match bevestigd</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1A2B4A] mt-4 leading-[1.05] tracking-tight">
                      {t("payment_success.title")} <span className="inline-block">{t("payment_success.title_emoji")}</span>
                    </h1>
                    <p className="font-heading text-2xl md:text-3xl font-bold text-[#F5A623] mt-2">
                      {t("payment_success.title_match")}
                    </p>
                    <p className="text-slate-600 mt-5 text-lg leading-relaxed">
                      {t("payment_success.subtitle")}
                    </p>
                  </div>
                  <div className="relative">
                    <div className="aspect-[4/3] rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl bg-white">
                      <img
                        src="https://images.pexels.com/photos/3184433/pexels-photo-3184433.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                        alt="Successful match"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-4 left-4 right-4 md:-bottom-5 md:left-8 md:right-8 bg-white rounded-2xl shadow-lg ring-1 ring-slate-100 p-4 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1A2B4A]">{matches.length} {matches.length === 1 ? "match" : "matches"} klaargezet</p>
                        <p className="text-xs text-slate-500">Zij nemen binnenkort contact met je op</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* MATCHES */}
            {matches.length > 0 && (
              <section className="max-w-6xl mx-auto px-4 md:px-8 pt-16 md:pt-20">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {matches.map((m) => m.kantoor && <KantoorCard key={m.id} kantoor={m.kantoor} />)}
                </div>
              </section>
            )}

            {/* SAVE MATCHES PORTAL CTA */}
            <section className="max-w-6xl mx-auto px-4 md:px-8 mt-12">
              <div className="beecenter-card p-6 md:p-8 bg-gradient-to-br from-amber-50/60 to-white">
                <div className="flex items-start gap-5 flex-wrap">
                  <div className="w-12 h-12 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center flex-shrink-0">
                    <BookmarkPlus size={22} />
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <h3 className="font-heading text-xl font-bold text-[#1A2B4A]">{t("payment_success.save_title")}</h3>
                    <p className="text-sm text-slate-600 mt-1">{t("payment_success.save_subtitle")}</p>
                  </div>
                  {user && user.role === "klant" ? (
                    <Link to="/mijn-matches" className="btn-amber whitespace-nowrap" data-testid="goto-mijn-matches">
                      {t("payment_success.go_portal")} <ArrowRight size={16} />
                    </Link>
                  ) : (
                    <button onClick={() => { if (klantId) sessionStorage.setItem("pending_klant_id", klantId); navigate("/login?role=klant"); }}
                      className="btn-amber whitespace-nowrap" data-testid="save-matches-login">
                      {t("payment_success.save_login")} <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            </section>

            {/* PARTNER SUGGESTIONS */}
            <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-24">
              <div className="text-center max-w-2xl mx-auto">
                <span className="pill-amber">{t("home.services.badge")}</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">
                  {t("payment_success.partners_title")}
                </h2>
                <p className="text-slate-600 mt-3">{t("payment_success.partners_subtitle")}</p>
              </div>
              <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
                {partners.map((p) => <PartnerCard key={p.id} partner={p} />)}
              </div>
            </section>

            {/* CONTACT / STAY CLOSE */}
            <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-24">
              <div className="rounded-3xl bg-[#1A2B4A] text-white p-8 md:p-12 relative overflow-hidden">
                <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-[#F5A623]/15 blur-3xl pointer-events-none" />
                <div className="relative grid md:grid-cols-2 gap-6 items-center">
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">{t("payment_success.stay_close_title")}</h3>
                    <p className="text-slate-300 mt-3">{t("payment_success.stay_close_desc")}</p>
                  </div>
                  <div className="flex md:justify-end">
                    <a href="mailto:info@beecentre.nl" className="btn-amber" data-testid="contact-cta">
                      <Mail size={16} /> {t("payment_success.contact_btn")}
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* TRUST STRIP */}
            <section className="max-w-6xl mx-auto px-4 md:px-8 mt-16 md:mt-20 mb-16">
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: ShieldCheck, t: t("payment_success.trust_independent_title"), d: t("payment_success.trust_independent_desc") },
                  { icon: Heart, t: t("payment_success.trust_personal_title"), d: t("payment_success.trust_personal_desc") },
                  { icon: Headphones, t: t("payment_success.trust_support_title"), d: t("payment_success.trust_support_desc") },
                ].map((it, i) => {
                  const Icon = it.icon;
                  return (
                    <div key={i} className="text-center md:text-left">
                      <div className="w-11 h-11 rounded-xl bg-[#F5A623]/10 text-[#F5A623] flex items-center justify-center mx-auto md:mx-0">
                        <Icon size={20} />
                      </div>
                      <h4 className="font-heading font-bold text-[#1A2B4A] mt-3">{it.t}</h4>
                      <p className="text-sm text-slate-600 mt-1">{it.d}</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-10 text-center">
                <Link to="/" className="btn-outline-navy" data-testid="back-home">
                  {t("payment_success.back_home")} <ArrowRight size={16} />
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
